import {
  forwardRef,
  useId,
  useState,
  type HTMLInputAutoCompleteAttribute,
  type HTMLInputTypeAttribute,
} from 'react';
import { InputWrapper, type BaseInputProps } from './InputWrapper';
import { cn } from '~/utils';

type InputProps = BaseInputProps & {
  type?: HTMLInputTypeAttribute;
  autoComplete?: HTMLInputAutoCompleteAttribute;
  renderRequiredSymbol?: boolean;
  min?: number | string;
  max?: number | string;
  step?: string | number;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      autoComplete,
      min,
      max,
      step,
      label,
      error,
      required,
      renderRequiredSymbol = required,
      disabled,
      containerClassName,
      leftIcon,
      rightIcon,
      rightAlign,
      onPaste,
      name,
      value,
      onChange,
      onFocus: onFocusCallback,
      onBlur: onBlurCallback,
      placeholder,
      onKeyDown,
      dataCy,
      className,
      ...restInputProps
    },
    ref
  ) => {
    const [focus, setFocus] = useState(false);
    const id = useId();
    const errorId = `${id}-error`;
    const hasError = error !== undefined && error !== '';

    const onFocus = () => {
      setFocus(true);
      onFocusCallback?.();
    };

    const onBlur = () => {
      onBlurCallback?.();
      setFocus(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.name;
      const value = e.target.value;
      onChange?.(name, value);
    };

    return (
      <InputWrapper
        label={label}
        id={id}
        errorId={errorId}
        error={error}
        required={required}
        renderRequiredSymbol={renderRequiredSymbol}
        disabled={disabled}
        containerClassName={containerClassName}
        focus={focus}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
      >
        <input
          className={cn(
            'bg-none border-none focus:outline-none hover:outline-none py-3 px-4 text-sm leading-[100%] font-normal w-full',
            rightAlign ? 'text-right' : '',
            className
          )}
          onPaste={onPaste}
          type={type}
          id={id}
          name={name}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          onFocus={onFocus}
          onBlur={onBlur}
          aria-disabled={disabled}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorId : undefined}
          aria-required={required}
          aria-labelledby={name}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={onKeyDown}
          ref={ref}
          min={min}
          max={max}
          step={step}
          data-cy={dataCy}
          {...restInputProps}
        />
      </InputWrapper>
    );
  }
);
Input.displayName = 'Input';
